import React, { useEffect, useRef, useState } from "react";
import { Send, Search } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";

export default function InternalChat() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const endRef = useRef(null);

  useEffect(() => { api.get("/chat/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);

  const loadThread = (c) => {
    setActive(c);
    api.get(`/chat/thread/${c.id}`).then((r) => setMsgs(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => api.get(`/chat/thread/${active.id}`).then((r) => setMsgs(r.data)).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const { data } = await api.post("/chat/send", { to_id: active.id, to_name: active.name, text });
    setMsgs((m) => [...m, data]); setText("");
  };

  const filtered = contacts.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">Team Chat</h1>
      <p className="text-slate-500 mt-1 text-sm">Message employees and agents directly.</p>

      <div className="bg-white rounded-2xl border border-slate-100 mt-5 grid md:grid-cols-3 h-[560px] overflow-hidden">
        <div className="border-r border-slate-100 flex flex-col">
          <div className="p-3 border-b border-slate-100 relative">
            <Search size={15} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people..." className="pl-9 h-10" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.id} onClick={() => loadThread(c)} className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#f7faf8] ${active?.id === c.id ? "bg-[#f0f6f3]" : ""}`}>
                <div className="w-9 h-9 rounded-full bg-[#004030] text-[#e0c05a] grid place-items-center text-sm font-bold">{c.name.slice(0, 1)}</div>
                <div><p className="text-sm font-medium text-[#012a1e]">{c.name}</p><p className="text-xs text-slate-400 capitalize">{c.kind}</p></div>
              </button>
            ))}
            {!filtered.length && <p className="text-center text-slate-400 text-sm py-8">No contacts.</p>}
          </div>
        </div>

        <div className="md:col-span-2 flex flex-col">
          {active ? (
            <>
              <div className="h-14 border-b border-slate-100 flex items-center gap-3 px-4">
                <div className="w-9 h-9 rounded-full bg-[#004030] text-[#e0c05a] grid place-items-center text-sm font-bold">{active.name.slice(0, 1)}</div>
                <div><p className="font-semibold text-[#012a1e] text-sm">{active.name}</p><p className="text-xs text-slate-400 capitalize">{active.kind}</p></div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#f7faf8]">
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.from_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${m.from_id === user.id ? "bg-[#004030] text-white rounded-br-sm" : "bg-white border border-slate-100 rounded-bl-sm"}`}>
                      {m.text}
                      <span className={`block text-[9px] mt-1 ${m.from_id === user.id ? "text-white/50" : "text-slate-400"}`}>{new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                ))}
                {!msgs.length && <p className="text-center text-slate-400 text-sm py-10">Say hello 👋</p>}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-slate-100 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." className="h-11" />
                <button onClick={send} className="w-11 h-11 rounded-full bg-[#004030] text-white grid place-items-center shrink-0"><Send size={18} /></button>
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-slate-400">Select a contact to start chatting</div>
          )}
        </div>
      </div>
    </div>
  );
}
